import { AppBuilderStreamAPIName, VisibilityRule } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type { MessagePart, SkillsConfig } from './builderSessionApi';

import { ApiMethods, ApiTags, appAPI, BUILDER_VIEW_CACHE_SECONDS } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'awaiting_config'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed'
  // Terminal, set when the user hard-cancels an in-flight build. Like 'failed'
  // it is a non-success terminal state, so it belongs in neither
  // BUILD_TIMELINE_COMPLETE_STATUSES nor PUBLISH_ENABLED_STATUSES below.
  | 'stopped';

// Statuses where the build has produced a runnable app — everything past
// 'generating' except the terminal 'failed'. The build timeline treats any of
// these as "build complete" (defense-in-depth against out-of-order SSE) even if
// a per-task update lagged. Deliberately broader than PUBLISH_ENABLED_STATUSES:
// the timeline shows complete as soon as the app exists, whereas publishing only
// enables once the build is ready/installed/deployed.
export const BUILD_TIMELINE_COMPLETE_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'awaiting_config',
  'deploying',
  'deployed',
  'installed',
];

// Statuses where the publish/deploy action is enabled — a strict subset of
// BUILD_TIMELINE_COMPLETE_STATUSES (see the note there for why they differ).
export const PUBLISH_ENABLED_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'installed',
  'deployed',
];

export interface RequiredSecret {
  key: string;
  label: string;
  description: string;
  /**
   * The input affordance the manifest asked for — `url`, `api_key`, `secret` or
   * `text`.
   *
   * NOT a closed set at runtime, which is why this is a plain string: the
   * manifest is LLM-authored and the backend stores whatever it says ("No
   * semantic enforcement on the backend" — `RequiredSecret.Type`). Apps have
   * declared e.g. `password`. Every consumer needs a default branch, and for a
   * credential that default must be the safe one — see inputTypeForSecret.
   */
  type: string;
  docsUrl?: string;
  // Platform-provisioned secrets (e.g. CRON_SECRET) the backend generates and
  // injects at deploy. Never rendered in the config form and never gate publish.
  managed?: boolean;
}

/**
 * A single OAuth-connected provider the generated app declares in
 * `.assembly/app.json` under `requiredIntegrations`. Mirrors the Go
 * `app_builder.RequiredIntegration` struct.
 */
export interface RequiredIntegration {
  provider: string;
  scopes: string[];
}

export type IntegrationStatus =
  | 'connected'
  | 'not_connected'
  | 'needs_reauth'
  | 'needs_reauth_scope';

/**
 * Connection state of a single declared integration. `connectedAt` and
 * `connectedByInternalUserId` are absent when `status` is `not_connected`.
 * The `needs_reauth` statuses mean a connection row exists but broker calls
 * fail closed until the user reconnects. Mirrors the Go
 * `app_builder.IntegrationStatusResponse` struct.
 */
export interface IntegrationStatusResponse {
  provider: string;
  scopes: string[];
  status: IntegrationStatus;
  connectedAt?: number;
  connectedByInternalUserId?: string;
}

/**
 * Parsed `.assembly/app.json` surface manifest expressed as paths.
 * Combined with `sandboxUrl` or `deploymentUrl` to construct preview / live
 * URLs. An empty string means the surface was not declared in the manifest;
 * `surfaces` itself being undefined means no manifest has been parsed yet
 * (legacy builders, or builds that haven't completed once).
 */
export interface AppSurfaces {
  internalPath?: string;
  clientPath?: string;
  notificationPath?: string;
}

export interface QualityReport {
  score: number;
  review: string;
  evaluatedAt?: string;
}

/**
 * A parent-detectable feature the app's bundled @assembly-js/app-bridge
 * advertises. Mirrors the capability keys the SDK bakes into its manifest;
 * add a member here when the parent needs to gate behavior on a new bridge
 * feature. Kept as a string-literal union so call sites stay honest.
 */
export type BridgeCapability = 'app.ready' | 'app.painted' | 'app.error';

/**
 * What the app's bundled SDKs can do, keyed by the component that provides
 * them. Derived from the app's committed manifests at build time on the
 * backend, so absence of a key means "not supported" — the correct reading
 * for older apps that predate a given capability.
 */
export interface AppCapabilities {
  bridge?: BridgeCapability[];
}

export type PreviewStatePhase =
  | 'booting'
  | 'compiling'
  | 'serving'
  | 'rendered'
  | 'painted'
  | 'rebuilding'
  | 'degraded'
  | 'dead';

export interface PreviewStateGrades {
  serving?: number;
  rendered?: number;
  painted: number | null;
}

/**
 * The reporter's verdict on one tokened page render: which preview token the
 * request carried (a sha256 prefix), its sequence within the reporter process
 * (tailerId), and the status the dev server answered with. The phase grades
 * describe the sandbox; this is the only field that names a document.
 */
export interface PreviewStatePageVerdict {
  seq: number;
  status?: number;
  at?: number;
  tokenHash?: string;
}

export interface PreviewStatePage extends PreviewStatePageVerdict {
  tailerId: string;
  /** Verdicts before this one, oldest first, so a co-viewer's request in the
   *  same tick cannot hide this viewer's. */
  recent?: PreviewStatePageVerdict[];
}

/** Full-snapshot preview readiness from the backend supervisor. */
export interface PreviewState {
  epoch: number;
  phase: PreviewStatePhase;
  reason?: string;
  url?: string;
  grades: PreviewStateGrades;
  /** Absent from snapshots folded by a reporter that predates the verdict. */
  page?: PreviewStatePage;
}

/**
 * Stamp recording that an app was saved as a Studio template, frozen at the
 * commit the template clones from.
 */
export interface PublishedTemplateStamp {
  commitSha: string;
  publishedAt: string;
  publishedBy: string;
}

/**
 * Response of POST /app-builders/:id/publish-template. Alongside the frozen
 * commit it carries a prefilled catalog card the curator pastes into Contentful
 * — there is no Contentful write path.
 */
export interface PublishTemplateOutput {
  templateId: string;
  name: string;
  icon: string;
  description: string;
  commitSha: string;
  publishedAt: string;
}

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  useProductAgent?: boolean;
  deploymentUrl: string;
  sandboxUrl?: string;
  // Durable connection state of the sandbox dev server. Single source of truth
  // for the reconnecting overlay — do NOT infer connection from sandboxUrl
  // (that URL is stable across stop/resume and says nothing about liveness).
  // Backend always returns a concrete value (legacy rows are derived).
  sandboxState?: SandboxConnectState;
  deployErrors?: string;
  // Granular publish stage while status === 'deploying' (one of the DeployPhase
  // values: 'preparing' | 'publishing' | 'finalizing'). Streamed from the deploy
  // job so the publish popover can show real progress. Empty/absent otherwise.
  deployPhase?: string;
  /**
   * Set when a duplicate came up usable but incomplete — its copied schema never
   * reached its own database, so DB-backed requests fail until a publish re-runs the
   * app's build. Rendered as CloneSetupWarningBanner in the builder composer
   * (not via deployErrors — that means build/publish failure). Cleared by the
   * backend on successful publish (see CloneSetupWarning on the AppBuilder model).
   */
  cloneSetupWarning?: string;
  /**
   * True while this app is a duplicate whose source code has not landed in its repo
   * yet — the window in which it is legitimately `generating` without a build or a
   * plan. `status` alone cannot express it: a duplicate's later edit turn is
   * `generating` too. Derived server-side from the copy marker, which is itself
   * never exposed (it names the source's repo).
   */
  cloneCopyPending?: boolean;
  /**
   * 'app' | 'template' when this builder was cloned. Derived from write-once
   * provenance so the editor can pick the first-boot narration variant —
   * duplicate copy for same-workspace duplicates, install copy for template
   * installs (which share cloneCopyPending / cloneStage).
   */
  cloneSourceType?: string;
  /**
   * Contentful catalog pointer saved at template install
   * (`CloneProvenance.templateId`). Used to resolve the installed-template
   * chat card. Distinct from the forked source app id so lookup survives
   * versioning. Absent on app duplicates and non-clones.
   */
  templateId?: string;
  /**
   * Template installs only; true when the source's workspace-scoped context
   * selections were dropped at clone time.
   */
  droppedSelections?: boolean;
  /**
   * Granular stage of a duplicate's one-time source-code copy
   * ('copying' | 'schema' | 'preview'). Empty/absent on a copy-pending app means
   * the sandbox boot — the first step of the narration, "Preparing your
   * workspace" — which is already running before the page paints and so needs no
   * push of its own.
   *
   * The duplicate narration ends on the pushed empty string, and on nothing
   * else (template installs instead settle on builder status / lastDeployedAt):
   * `cloneCopyPending` flips false at the copy's ready flip, and a concurrent
   * reconnect can publish `sandboxState: 'connected'`, both of them BEFORE
   * 'preview' arrives. Ending on either leaves the last step unrendered.
   * Written only by the job that owns the copy, so `sandboxState` must not be
   * read as implying the earlier stages finished either.
   */
  cloneStage?: string;
  installedAppId: string;
  installedModuleId: string;
  modelConfig?: ModelConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requiredSecrets?: RequiredSecret[];
  /**
   * Declared, non-managed secret keys that currently hold a value. Keys only —
   * values never cross the API boundary, so this is the only way to tell a
   * configured secret from a blank one. Absent on legacy rows and on apps whose
   * manifest declares no secrets; treat undefined as "none configured".
   */
  configuredSecretKeys?: string[];
  /**
   * Declared, non-managed secret keys whose stored value differs from what the
   * live app is running — saved, but not yet published. Empty for an app that
   * has never deployed. Keys only, for the same reason as above.
   */
  pendingSecretKeys?: string[];
  requiredIntegrations?: RequiredIntegration[];
  surfaces?: AppSurfaces;
  // Internal performance metric anchors (POR-20126). Both nullable —
  // populated lazily on first plan turn / first ready transition, so
  // older AppBuilder rows will have them undefined.
  buildStartedAt?: string;
  firstReadyAt?: string;
  // Provider slugs that were connected when the build first reached ready — the
  // frozen snapshot the build-complete copy uses so its connection sentence stays
  // true to that moment, immune to a later connect/disconnect. Only ever present
  // and non-empty (a build that reused an existing connection). Absent means no
  // snapshot — a legacy row, or a build with nothing connected at ready — and the
  // copy falls back to live connection state. See backend
  // AppBuilder.ConnectedSlugsAtReady.
  connectedSlugsAtReady?: string[] | null;
  // ISO timestamp of the last successful deploy to clients. Absent on legacy
  // rows and apps that have never published; drives the "Last published …"
  // label in the builder header.
  lastDeployedAt?: string;
  // The structural layout the product agent chose. Absent when the product
  // agent was not used or did not finalize a layout. Drives the ?layout=
  // query param on the preview iframe so the sandbox's foundation page renders
  // a matching loading skeleton on first paint.
  layout?: {
    archetype: string;
    hasFilterBar: boolean;
    hasHeaderCta: boolean;
    hasTabs: boolean;
    regions: string[];
    contentWidth?: 'full' | 'narrow';
  };
  benchmarkRunId?: string;
  qualityReport?: QualityReport;
  hasUnpublishedChanges?: boolean | null;
  // Absent for every app never saved as a template, so a presence check is the
  // single signal for saved-vs-not.
  publishedAsTemplate?: PublishedTemplateStamp;
  // Preview deployment fields — set and streamed by the preview-deploy job.
  // URL and timestamp are present when a fresh preview exists; phase is non-empty
  // while a deploy is in flight; errors holds the Vercel message when the last
  // preview deploy failed. All four are cleared when hasUnpublishedChanges flips.
  previewDeploymentUrl?: string;
  previewDeployedAt?: string;
  previewDeployPhase?: string;
  previewDeployErrors?: string;
  // What the deployed app's bundled SDKs support, stamped at build time.
  // Undefined for legacy builders predating capability reporting.
  capabilities?: AppCapabilities;
  // Monotonic counter bumped at every preview-URL mint. Supervisor snapshots
  // carry the same epoch so a consumer can drop stale events.
  previewStateEpoch?: number;
  // Last full preview-readiness snapshot. Present once the supervisor has
  // ticked; the TICK pull and SSE resync both read this object.
  previewState?: PreviewState;
  // Name/icon/visibility edits made in the publish popover that are deferred
  // until the deploy succeeds. Present only between a publish dispatch and its
  // completion (or after a failed publish, so the form can re-populate).
  pendingPublishSettings?: PendingPublishSettings;
  // A pending platform-authored update this app is eligible for. Absent when the enable-app-updates
  // flag is off, no registry update applies, or one is already in flight —
  // never a failed GET, so a boolean check must not distinguish those cases.
  availableUpdate?: AvailableUpdate;
}

export interface AvailableUpdate {
  type: string;
  title: string;
  description: string;
}

/**
 * The publish-popover edits carried along with a deploy so the backend applies
 * them at the atomic success moment (in lockstep with the new version going
 * live) rather than before the deploy finishes. Each field is optional — only
 * the settings the user actually changed are sent.
 */
export interface PendingPublishSettings {
  name?: string;
  icon?: string;
  visibilityConfig?: VisibilityRule[];
}

/**
 * Presence check for a bridge capability. Returns false when the builder is
 * undefined or the capability isn't listed — i.e. old apps and apps built
 * before the capability existed are treated as not supporting it.
 */
export const hasBridgeCapability = (
  builder: AppBuilder | undefined,
  capability: BridgeCapability,
): boolean => builder?.capabilities?.bridge?.includes(capability) ?? false;

export interface SkillInfo {
  name: string;
  description: string;
}

export interface PromptInfo {
  filename: string;
  content: string;
}

export interface PromptSlotVersion {
  id: string;
  title: string;
  version: number;
  isDefault: boolean;
  updatedAt: string;
}

export interface PromptSlot {
  slug: string;
  label: string;
  category: string;
  defaultContent: string;
  versions: PromptSlotVersion[] | null;
}

export interface BuildConfigResponse {
  skills: SkillInfo[];
  prompts: {
    planner: PromptInfo;
    builder: PromptInfo;
  };
  slots?: PromptSlot[];
}

export interface UpdateAppBuilderInput {
  id: string;
  name?: string;
  icon?: string;
}

/**
 * Reference to an uploaded attachment file. The webapp uploads files
 * directly to the user-files bucket via Amplify Storage.put at
 * level=protected, then echoes the resulting relative fileKey plus the
 * uploader's Cognito identityId back to the backend on the sendMessage
 * call. The backend reconstructs the full S3 path on its end when
 * presigning GETs for the sandbox turn program.
 */
export interface AppBuilderAttachment {
  id: string;
  name: string;
  fileKey: string;
  identityId: string;
  contentType?: string;
  size?: number;
}

export const isValidAttachment = (
  value: unknown,
): value is AppBuilderAttachment => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (['id', 'name', 'fileKey', 'identityId'] as const).every(
    (field) => typeof record[field] === 'string' && record[field] !== '',
  );
};

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'send_followup'
  | 'skip'
  | 'allow';

export interface AppBuilderMessage {
  id: string;
  content: string;
  type: string;
  role: string;
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  actionHints?: AppBuilderMessageActionHint[];
  /** Raw message parts from the builder session API. Populated for task_list messages. */
  parts?: MessagePart[];
  narratorStatus?: 'forming' | 'complete';
  /** 0-based index of the thinking paragraph this narration summarizes; the
   * Decision Stream orders lines by it (stable across out-of-order completions). */
  narratorParagraphIndex?: number;
  attachments?: AppBuilderAttachment[];
  /** Client-only: the builder session an optimistic bubble was created for, so
   * the bubble can be filtered out of any other session's thread. Real persisted
   * messages are already session-guarded upstream in useBuilderSessionSSE. */
  builderSessionId?: string;
  /** Client-only: the id this optimistic send was minted with and sent to the
   * backend as `clientMessageId`. The backend adopts it as the persisted
   * message's id, so the bubble (and its durable mirror) reconcile by exact id
   * rather than content. Absent on server messages. */
  clientMessageId?: string;
  /** First message of a fresh context after a "New session" reset. The
   * transcript draws the new-session divider directly above this message —
   * anchored by identity, never by timestamp ordering. */
  startsNewSession?: boolean;
  /** Internal user id of the teammate who sent a user message. Absent on
   * legacy rows and optimistic bubbles, which both render as the viewer's own. */
  sentBy?: string;
}

export interface AppBuilderVersionDiff {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  changedFiles: string[];
  addedFiles?: string[];
  removedFiles?: string[];
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
  // Both default to true when undefined; the card applies the defaulting.
  allowFreeText?: boolean;
  skippable?: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message as string;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export interface PlanTask {
  id: string;
  /** Human-readable headline shown to non-technical reviewers. */
  title: string;
  /** 1–2 sentence non-technical summary of what this task delivers. */
  userSummary: string;
  /** Technical implementation instructions for the build agent (LLM). */
  description: string;
  stepsToVerify: string[];
}

export const isPlanTask = (value: unknown): value is PlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.userSummary === 'string' &&
    typeof v.description === 'string' &&
    Array.isArray(v.stepsToVerify) &&
    v.stepsToVerify.every((s) => typeof s === 'string')
  );
};

export const isPlanTaskArray = (value: unknown): value is PlanTask[] =>
  Array.isArray(value) && value.every(isPlanTask);

export interface BuildEvent {
  type:
    | 'task_started'
    | 'task_log'
    | 'task_completed'
    | 'task_failed'
    | 'deploying'
    | 'completed'
    | 'error';
  taskId?: string;
  line?: string;
  url?: string;
  message?: string;
  token?: string;
}

export interface ModelConfig {
  planningModel?: string;
  planEffort?: string;
  buildModel?: string;
  buildEffort?: string;
  /**
   * buildModel/buildEffort came from the default rather than a pin, so the
   * picker can show Auto instead of the model Auto happens to resolve to.
   */
  autoMode?: boolean;
  noTaskList?: boolean;
  skipParallelism?: boolean;
}

export interface PrepareBuildOutput {
  appBuilderId: string;
  sandboxName: string;
  repoFullName: string;
  marketplaceAppId: string;
  // BuilderSession created alongside the AppBuilder; the kickoff flow
  // navigates to this session instead of issuing a separate create call.
  sessionId: string;
}

export interface StartBuildOutput {
  sessionId: string;
  activeTaskListMessageId: string;
}

export async function prepareBuild(
  appName?: string,
  modelConfig?: ModelConfig,
  prompt?: string,
  useProductAgent?: boolean,
  skillsConfig?: SkillsConfig,
  promptSelections?: Record<string, string>,
): Promise<PrepareBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      appName,
      modelConfig,
      prompt,
      useProductAgent,
      ...(skillsConfig ? { skillsConfig } : {}),
      ...(promptSelections ? { promptSelections } : {}),
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(AppBuilderStreamAPIName, '/app-builders/prepare-build'),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<PrepareBuildOutput>;
}

export async function startBuild(
  appBuilderId: string,
  sessionId: string,
): Promise<StartBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/build`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<StartBuildOutput>;
}

export interface AcceptAppUpdateOutput {
  sessionId: string;
  turnId: string;
  updateType: string;
}

/** Accepts the registry update currently offered on this app (see
 * `availableUpdate` on the AppBuilder GET response) and dispatches the
 * update turn. No request body — the offered update is server-resolved. */
export async function acceptAppUpdate(
  appBuilderId: string,
): Promise<AcceptAppUpdateOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/update`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<AcceptAppUpdateOutput>;
}

export async function retryBuild(
  appBuilderId: string,
  sessionId: string,
): Promise<void> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/sessions/${sessionId}/retry`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }
}

/** Re-wake and re-dispatch for an existing user message or plan approval
 *  (no duplicate user row). */
export type RetryTurnSetupParams = {
  messageId?: string;
  approvalMode?: boolean;
  approvedMsgId?: string;
  failedTurnId?: string;
};

export async function retryTurnSetup(
  appBuilderId: string,
  sessionId: string,
  params: RetryTurnSetupParams,
): Promise<void> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(params.messageId ? { messageId: params.messageId } : {}),
      ...(params.approvalMode ? { approvalMode: true } : {}),
      ...(params.approvedMsgId ? { approvedMsgId: params.approvedMsgId } : {}),
      ...(params.failedTurnId ? { failedTurnId: params.failedTurnId } : {}),
    }),
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/sessions/${sessionId}/retry-setup`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export const getAppBuilderIntegrationsTag = (id: string) => ({
  type: ApiTags.appBuilderIntegrations as const,
  id,
});

export type SandboxConnectState =
  | 'connected'
  | 'connecting'
  | 'refreshing'
  | 'disconnected'
  | 'error';

export interface ReconnectSandboxResponse {
  // The synchronous reconnect endpoint only ever returns connected or
  // connecting; the async job later drives the builder's sandboxState to
  // connected or disconnected.
  state: 'connected' | 'connecting';
  sandboxUrl?: string;
}

/** Which viewer the QA testing agent should be minted a session token as. */
export type QAEmbedIdentity = 'internal' | 'client';

/** Which host renders the embed — see QAEmbedInput.Target in interfaces.go. */
export type QAEmbedTarget = 'deployed' | 'sandbox';

export interface QAEmbedInput {
  id: string;
  identity: QAEmbedIdentity;
  internalUserId?: string;
  clientUserId?: string;
  companyId?: string;
  // NotificationID, when set, mints the notification surface instead of the
  // surface `identity` picks. Requires identity="internal" (see
  // ErrNotificationRequiresInternalIdentity in errors.go).
  notificationId?: string;
  instanceId?: string;
  target?: QAEmbedTarget;
}

export interface QAEmbedOutput {
  embedUrl: string;
  identity: QAEmbedIdentity;
  appId: string;
  portalId: string;
}

/** One chat turn between a portal user and the QA drafting agent. Mirrors
 * the Go `app_builder.QASessionMessage` struct. */
export interface QASessionMessage {
  role: string;
  content?: string;
  createdAt: string;
}

export type QASessionStatus = 'active' | 'closed';

/**
 * Durable state behind one QA tab conversation: the chat transcript with the
 * QA drafting agent and the test-doc draft it is iterating on. Mirrors the
 * Go `app_builder.QASession` model. `draftTestDoc` is opaque JSON owned by
 * the QA drafting agent (the qa/agent flows contract: flows with intent,
 * acceptance criteria, identity, surface).
 */
export interface QASession {
  id: string;
  object: 'qaSession';
  appBuilderId: string;
  portalId: string;
  status?: QASessionStatus;
  messages?: QASessionMessage[];
  draftTestDoc?: unknown;
  createdAt: string;
  updatedAt?: string;
}

export type QARunStatus = 'queued' | 'running' | 'passed' | 'failed' | 'error';

/**
 * One QA agent run dispatched against a QASession's draft test doc. Mirrors
 * the Go `app_builder.QARun` model. Verdict counts are only meaningful once
 * the run reaches a terminal status.
 */
export interface QARun {
  id: string;
  object: 'qaRun';
  qaSessionId: string;
  appBuilderId: string;
  runId: string;
  status?: QARunStatus;
  passCount?: number;
  failCount?: number;
  blockedCount?: number;
  errorCount?: number;
  skippedCount?: number;
  costUsd?: number;
  budgetUsd?: number;
  // target is one of 'live' | 'preview'; absent on runs dispatched before this
  // field existed — treat absent as 'live'.
  target?: string;
  // targetUrl is the origin URL of the deployment this run tested, snapshotted
  // at dispatch time.
  targetUrl?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ListQASessionsResponse {
  data: QASession[];
  nextToken?: string;
}

export interface ListQARunsResponse {
  data: QARun[];
  nextToken?: string;
}

/** One entry in the egress outbox as seen in a QA review flow. */
export interface QAReviewFlowOutboxEntry {
  channel: string;
  recipient: string;
  subject?: string;
  createdAt: string;
}

/** Match result for one outbox expectation in a QA review flow. */
export interface QAReviewFlowExpectationResult {
  expectation: {
    channel: string;
    recipient?: string;
    subject?: string;
    note?: string;
  };
  matched: boolean;
  matches: QAReviewFlowOutboxEntry[];
}

/** Outbox evaluation summary attached to a flow that declared expectOutbox. */
export interface QAReviewFlowOutbox {
  expectations: QAReviewFlowExpectationResult[];
  entriesSeen: number;
  fetched: boolean;
}

/** One flow within a QA run: its definition plus the agent's verdict and a
 * short-lived presigned URL to its screen recording. Mirrors the Go
 * `app_builder.QAReviewFlow` struct. */
export interface QAReviewFlow {
  id: string;
  name: string;
  intent: string;
  criteria: string[];
  status: string;
  evidence: string;
  notes: string;
  videoUrl: string;
  outbox?: QAReviewFlowOutbox;
}

/** Response of `GET /app-builders/:id/qa-review`: a run's per-flow verdicts
 * and short-lived presigned recording URLs. Defaults to the latest run when
 * `runId` is omitted. Mirrors the Go `app_builder.QAReviewOutput` struct. */
export interface QAReviewOutput {
  builderId: string;
  runId: string;
  passed: number;
  total: number;
  flows: QAReviewFlow[];
}

export const getQASessionListTag = (id: string) => ({
  type: ApiTags.qaSessions as const,
  id,
});

export const getQARunListTag = (id: string) => ({
  type: ApiTags.qaRuns as const,
  id,
});

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      keepUnusedDataFor: BUILDER_VIEW_CACHE_SECONDS,
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<
      AppBuilder,
      { id: string; pendingSettings?: PendingPublishSettings }
    >({
      query: ({ id, pendingSettings }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        // Only send a body when there are deferred edits, so a plain publish
        // stays a bodyless request.
        options: pendingSettings ? { body: pendingSettings } : {},
      }),
      // Optimistically stamp the deferred edits onto the cached builder so the
      // publish popover shows the new name/icon/visibility immediately while the
      // deploy runs — the backend persists the same pendingPublishSettings, so
      // the follow-up refetch (via the invalidated tag) confirms it. Reverted if
      // the deploy request itself fails.
      async onQueryStarted(
        { id, pendingSettings },
        { dispatch, queryFulfilled },
      ) {
        if (!pendingSettings) return;
        // An empty body is a "clear" signal (the user reverted their edits), so
        // reflect that by dropping the cached pending settings.
        const isClear =
          pendingSettings.name === undefined &&
          pendingSettings.icon === undefined &&
          pendingSettings.visibilityConfig === undefined;
        const patch = dispatch(
          appBuilderApi.util.updateQueryData(
            'getAppBuilder',
            { id },
            (draft) => {
              draft.pendingPublishSettings = isClear
                ? undefined
                : pendingSettings;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    submitAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.post,
        options: { body: { values } },
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    // PARTIAL update: only the keys present in `values` are written, and every
    // omitted key keeps its stored value. Distinct from submitAppBuilderSecrets
    // above, which is the awaiting_config gate's all-at-once POST and deploys on
    // success. The response never carries values — the useful parts are the
    // refreshed configuredSecretKeys and hasUnpublishedChanges. Refused with a
    // 409 while the app is publishing.
    updateAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.patch,
        options: { body: { values } },
      }),
      // The LIST tag is what makes a save on one surface show up on the other:
      // the settings page reads the builders list, the build page reads the
      // entity. No optimistic update — claiming a credential was stored before
      // the backend confirmed it is the wrong way to be wrong.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    createIntegrationConnectSession: build.mutation<
      { sessionToken: string },
      { id: string; provider: string }
    >({
      query: ({ id, provider }) => ({
        path: `/app-builders/${id}/integrations/${provider}/connect-session`,
        method: ApiMethods.post,
        options: {},
      }),
    }),
    submitIntegrationApiKey: build.mutation<
      IntegrationStatusResponse[],
      { id: string; provider: string; apiKey: string }
    >({
      query: ({ id, provider, apiKey }) => ({
        path: `/app-builders/${id}/integrations/${provider}/api-key`,
        method: ApiMethods.post,
        options: { body: { apiKey } },
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      // The resulting connection is workspace-scoped, so the Settings
      // Connections page caches must refresh too. The builder's own
      // integrations list is refetched by the caller (mirroring the OAuth
      // connect flow), not via tags.
      invalidatesTags: [
        ApiTags.portalConnections,
        ApiTags.portalConnectionsAvailable,
      ],
    }),
    // The `basic` analogue of submitIntegrationApiKey: same synchronous import
    // contract, but the credential is a username/password pair (Twilio
    // repurposes it as Account SID + Auth Token).
    submitIntegrationBasicAuth: build.mutation<
      IntegrationStatusResponse[],
      { id: string; provider: string; username: string; password: string }
    >({
      query: ({ id, provider, username, password }) => ({
        path: `/app-builders/${id}/integrations/${provider}/basic`,
        method: ApiMethods.post,
        options: { body: { username, password } },
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      invalidatesTags: [
        ApiTags.portalConnections,
        ApiTags.portalConnectionsAvailable,
      ],
    }),
    getAppBuilderIntegrations: build.query<
      IntegrationStatusResponse[],
      { id: string }
    >({
      query: ({ id }) => ({
        path: `/app-builders/${id}/integrations`,
        method: ApiMethods.get,
        options: {},
      }),
      transformResponse: (response: {
        integrations: IntegrationStatusResponse[];
      }) => response.integrations,
      providesTags: (_, __, arg) => [getAppBuilderIntegrationsTag(arg.id)],
    }),
    updateAppBuilder: build.mutation<AppBuilder, UpdateAppBuilderInput>({
      query: ({ id, ...body }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.patch,
        options: { body },
      }),
      // A rename updates the linked install's label in module_settings, so the
      // installs (apps) cache that drives sidebar rendering must be refreshed.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        ApiTags.apps,
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      // The backend's app-builder delete also removes the linked install from
      // module_settings, so invalidate the installs (apps) cache too.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    transferAppBuilder: build.mutation<
      AppBuilder,
      // destinationUserId is required: every row the move re-keys also carries
      // an actor, and the requester exists only in the source workspace.
      { id: string; destinationPortalId: string; destinationUserId: string }
    >({
      query: ({ id, destinationPortalId, destinationUserId }) => ({
        path: `/app-builders/${id}/transfer`,
        method: ApiMethods.post,
        options: { body: { destinationPortalId, destinationUserId } },
      }),
      // A transfer moves the app out of this workspace entirely, so drop it
      // from the app-builder list and refresh the installs (apps) cache that
      // drives sidebar rendering.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    // The UI verb is 'Save as template'; the route is still publish-template.
    saveAppBuilderAsTemplate: build.mutation<
      PublishTemplateOutput,
      { id: string }
    >({
      query: ({ id }) => ({
        path: `/app-builders/${id}/publish-template`,
        method: ApiMethods.post,
        options: {},
      }),
      // Only the builder gains a stamp — saving leaves the installs and apps
      // caches untouched, unlike a transfer which moves the app out entirely.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    cloneAppBuilder: build.mutation<
      PrepareBuildOutput,
      // Exactly one of sourceAppBuilderId / templateId — the backend
      // rejects both or neither. Duplicate uses the former; template install
      // (Browse modal + marketing handoff) uses the latter.
      | { sourceAppBuilderId: string; name?: string }
      | { templateId: string; name?: string }
    >({
      query: (body) => ({
        path: '/app-builders/clone',
        method: ApiMethods.post,
        options: { body },
      }),
      // A clone creates both a new builder row and a new install row, so refresh
      // the app-builder list alongside the installs (apps) cache that drives the
      // sidebar and the app-setup list. Template installs also advance Studio
      // firstApp server-side — refetch onboarding so the checklist updates.
      invalidatesTags: [
        ApiTags.appBuilders,
        ApiTags.apps,
        ApiTags.onboarding_task_status,
      ],
    }),
    getBuildConfig: build.query<BuildConfigResponse, void>({
      query: () => ({
        path: '/app-builders/build-config',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [{ type: ApiTags.contextItems, id: 'LIST' }],
    }),
    reconnectSandbox: build.mutation<ReconnectSandboxResponse, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/sandbox/reconnect`,
        method: ApiMethods.post,
        options: {},
      }),
      // Patch the cached builder rather than invalidating its tag. The response
      // carries exactly what must surface immediately (the overlay's
      // sandboxState), and reconnect fires on every app open, so its response
      // often lands after the user has switched away —
      // an invalidation then hits a subscriber-less cache entry, which RTK
      // Query DELETES, and switching back drops the whole builder view to the
      // page skeleton on the refetch.
      // Not destructured: pulling getState off the lifecycle object trips
      // @typescript-eslint/unbound-method.
      async onQueryStarted({ id }, lifecycle) {
        // Snapshot before the request resolves: the probe exists to correct a
        // STALE cached 'connected' (a reaped sandbox behind a warm cache), so
        // that state must not veto the response. Only a 'connected' that
        // appears while this request is in flight — the async wake job's SSE
        // push landing first — is newer than the response and must win, since
        // getAppBuilder has no poll to correct a regression and the preview
        // stays blocked on anything but 'connected'.
        const stateBefore = appBuilderApi.endpoints.getAppBuilder.select({
          id,
        })(lifecycle.getState()).data?.sandboxState;
        try {
          const { data } = await lifecycle.queryFulfilled;
          lifecycle.dispatch(
            appBuilderApi.util.updateQueryData(
              'getAppBuilder',
              { id },
              (draft) => {
                const connectedLandedMidFlight =
                  draft.sandboxState === 'connected' &&
                  stateBefore !== 'connected';
                if (!connectedLandedMidFlight) {
                  draft.sandboxState = data.state;
                }
                if (data.sandboxUrl) draft.sandboxUrl = data.sandboxUrl;
              },
            ),
          );
        } catch {
          // Reconnect failed — leave the cache alone; the status poll and SSE
          // preview_state remain the source of truth.
        }
      },
    }),
    stopBuilderTurn: build.mutation<
      StopTurnResponse,
      { id: string; sessionId: string }
    >({
      query: ({ id, sessionId }) => ({
        path: `/app-builders/${id}/sessions/${sessionId}/stop`,
        method: ApiMethods.post,
        options: {},
      }),
      // Refetch the builder so the status flip (generating → stopped) surfaces
      // immediately rather than on the next poll.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    getQAEmbed: build.query<QAEmbedOutput, QAEmbedInput>({
      query: ({ id, ...params }) => ({
        path: `/app-builders/${id}/qa-embed`,
        method: ApiMethods.get,
        options: {
          // Mirrors QAEmbedInput in backend/lib/resources/app_builder/interfaces.go
          // — every field is forwarded as-is, and GetQAEmbed's permutation
          // table decides which combination is a valid identity. Undefined
          // fields are dropped rather than sent as the literal string
          // "undefined", which the backend would treat as a non-empty value.
          queryStringParameters: Object.fromEntries(
            Object.entries(params).filter(([, value]) => value !== undefined),
          ),
        },
      }),
    }),
    createQASession: build.mutation<QASession, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/qa-sessions`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [getQASessionListTag(arg.id)],
    }),
    listQASessions: build.query<
      ListQASessionsResponse,
      { id: string; limit?: number; nextToken?: string }
    >({
      query: ({ id, ...params }) => ({
        path: `/app-builders/${id}/qa-sessions`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: Object.fromEntries(
            Object.entries(params).filter(([, value]) => value !== undefined),
          ),
        },
      }),
      providesTags: (_, __, arg) => [getQASessionListTag(arg.id)],
    }),
    getQASession: build.query<QASession, { id: string; sessionId: string }>({
      query: ({ id, sessionId }) => ({
        path: `/app-builders/${id}/qa-sessions/${sessionId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [
        { type: ApiTags.qaSessions as const, id: arg.sessionId },
      ],
    }),
    listQARuns: build.query<
      ListQARunsResponse,
      { id: string; limit?: number; nextToken?: string }
    >({
      query: ({ id, ...params }) => ({
        path: `/app-builders/${id}/qa-runs`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: Object.fromEntries(
            Object.entries(params).filter(([, value]) => value !== undefined),
          ),
        },
      }),
      providesTags: (_, __, arg) => [getQARunListTag(arg.id)],
    }),
    // Defaults to the latest run when `runId` is omitted (see
    // logic_qa_review.go's latestQARunID) — pass it to replay any past run
    // from the run history list.
    getQAReview: build.query<QAReviewOutput, { id: string; runId?: string }>({
      query: ({ id, runId }) => ({
        path: `/app-builders/${id}/qa-review`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: runId ? { runId } : {},
        },
      }),
    }),
    // Sends one user turn to the QA flow drafter and returns the session with
    // the agent's reply and its iterated draft appended. Invalidating the
    // session tag refetches getQASession's transcript + draftTestDoc.
    createQASessionMessage: build.mutation<
      QASession,
      { id: string; sessionId: string; message: string }
    >({
      query: ({ id, sessionId, message }) => ({
        path: `/app-builders/${id}/qa-sessions/${sessionId}/messages`,
        method: ApiMethods.post,
        options: { body: { message } },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.qaSessions as const, id: arg.sessionId },
      ],
    }),
    // Closes the session, freezing its draft as the input runs dispatch
    // against. Invalidating the session tag flips getQASession's status to
    // 'closed', which locks chat and unlocks "Run these tests".
    approveQASession: build.mutation<
      QASession,
      { id: string; sessionId: string }
    >({
      query: ({ id, sessionId }) => ({
        path: `/app-builders/${id}/qa-sessions/${sessionId}/approve`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.qaSessions as const, id: arg.sessionId },
      ],
    }),
    // Kicks off an async Vercel preview deploy for this builder. The builder's
    // previewDeployPhase field is streamed while in flight; previewDeploymentUrl
    // and previewDeployedAt are set on success; previewDeployErrors on failure.
    previewDeploy: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/preview-deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    // Materializes the session's approved draft into a run and launches the ECS
    // task. Invalidating the run-list tag surfaces the new queued run in the
    // history immediately; callers poll getQARun for its terminal status.
    dispatchQARun: build.mutation<
      QARun,
      { id: string; sessionId: string; budgetUsd?: number; target?: string }
    >({
      query: ({ id, sessionId, budgetUsd, target }) => ({
        path: `/app-builders/${id}/qa-sessions/${sessionId}/qa-runs`,
        method: ApiMethods.post,
        options: {
          body: {
            ...(budgetUsd ? { budgetUsd } : {}),
            ...(target ? { target } : {}),
          },
        },
      }),
      invalidatesTags: (_, __, arg) => [getQARunListTag(arg.id)],
    }),
    // One run's live status. Polled (pollingInterval) until terminal so the
    // history row and results panel update without a manual refetch.
    getQARun: build.query<QARun, { id: string; runId: string }>({
      query: ({ id, runId }) => ({
        path: `/app-builders/${id}/qa-runs/${runId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [
        { type: ApiTags.qaRuns as const, id: arg.runId },
      ],
    }),
  }),
});

export interface StopTurnResponse {
  sessionId: string;
  turnId?: string;
  stopped: boolean;
}

export const {
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useDeployAppBuilderMutation,
  useSubmitAppBuilderSecretsMutation,
  useUpdateAppBuilderSecretsMutation,
  useCreateIntegrationConnectSessionMutation,
  useSubmitIntegrationApiKeyMutation,
  useSubmitIntegrationBasicAuthMutation,
  useGetAppBuilderIntegrationsQuery,
  useUpdateAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useTransferAppBuilderMutation,
  useSaveAppBuilderAsTemplateMutation,
  useCloneAppBuilderMutation,
  useGetBuildConfigQuery,
  useReconnectSandboxMutation,
  useStopBuilderTurnMutation,
  useGetQAEmbedQuery,
  useCreateQASessionMutation,
  useListQASessionsQuery,
  useGetQASessionQuery,
  useListQARunsQuery,
  useGetQAReviewQuery,
  useCreateQASessionMessageMutation,
  useApproveQASessionMutation,
  usePreviewDeployMutation,
  useDispatchQARunMutation,
  useGetQARunQuery,
} = appBuilderApi;
